import React from "react";
import moment from "moment/moment";
import {
  loadUser,
  getUserCashflow,
  calculateCashflow,
  changePassword,
  logout,
} from "../api";
import { useNotifications } from "../helpers/useNotifications";
import { useQuery } from "react-query";
import Spinner from "../components/Spinner/Spinner";
import Image from "../components/Image/Image";
import Grid from "../components/Grid/Grid";
import parse from "html-react-parser";
import Fox from "../../src/FoxDark.PNG";
import formatNumber from "../helpers/formatNumber";
import Charts from "../components/Charts/Charts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyBillTrendUp,
  faForward,
  faTrophy,
  faSquareRootVariable,
} from "@fortawesome/free-solid-svg-icons";

const CashflowDescription = ({ income, expense }) => {
  if (income > expense)
    return (
      <>
        Damit bleiben dir
        <b className="visualizeLink linkIsInformative">{` ${formatNumber(
          (income - expense).toFixed(2),
        )}€ `}</b>
        übrig.
      </>
    );

  if (income < expense)
    return (
      <p>
        Somit übersteigen deine Ausgaben dein Budget um
        <b className="visualizeLink linkIsInformative">{` ${formatNumber(
          (expense - income).toFixed(2),
        )}€`}</b>
        .
      </p>
    );

  return <p>Deine Ausgaben werden von deinem Budget gedeckt.</p>;
};

//* create tooltip
const createTooltip = (name, value) => `${name}: ${formatNumber(value)}€`;

//* summarize all values by category
const sumCategory = (data) =>
  data.reduce((acc, el) => {
    if (!acc[el.categoryTitle]) acc[el.categoryTitle] = 0;
    acc[el.categoryTitle] += el.value;
    return acc;
  }, {});

//* scroll to calculation
const scrollToBottom = () => {
  const submitButton = document.getElementById("calculationSubmit");
  if (submitButton) {
    submitButton.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};

function Userprofile() {
  //* init
  const createNotification = useNotifications();

  //* data fetching
  const { data, isLoading } = useQuery(["userprofile"], () => loadUser());
  const { data: dbCashflow } = useQuery(["usercashflow"], () =>
    getUserCashflow(),
  );

  //* states
  const [sankeyChartData, setSankeyChartData] = React.useState();
  const [totalAmount, setTotalAmount] = React.useState();
  const [showUserPasswordEdit, setShowUserPasswordEdit] = React.useState(false);
  const [changePasswordFormItem, setChangePasswordFormItem] = React.useState({
    old: null,
    new: null,
    repeat: null,
  });

  //* Effect: calculate cashflow once dbCashflow is loaded
  React.useEffect(() => {
    if (!dbCashflow) return;

    const fetchCashflow = async () => {
      try {
        const cashflowArray = Object.entries(dbCashflow).map(
          ([key, value]) => ({
            id: key,
            ...value,
          }),
        );

        const result = await calculateCashflow(JSON.stringify(cashflowArray));

        const chartData = [
          ["From", "To", "Summe", { type: "string", role: "tooltip" }],
        ];

        const allExpenses = result?.data?.filter(
          (el) => el.category !== "income",
        );
        const categoryData = sumCategory(allExpenses);

        Object.entries(categoryData)?.forEach(([categoryTitle, value]) => {
          chartData.push([
            "Budget",
            categoryTitle,
            value,
            createTooltip(categoryTitle, value),
          ]);
        });

        result?.data?.forEach((el) => {
          if (el.category === "income") {
            chartData.push([
              el.id,
              "Budget",
              el.value,
              createTooltip(el.id, el.value),
            ]);
          } else {
            chartData.push([
              el.categoryTitle,
              el.id,
              el.value,
              createTooltip(el.id, el.value),
            ]);
          }
        });

        setSankeyChartData(chartData);
        setTotalAmount({
          income: result?.totalIncome,
          expense: result?.totalExpense,
        });

        scrollToBottom();
      } catch (error) {
        console.error("Fehler beim Berechnen des Cashflows:", error);
      }
    };

    fetchCashflow();
  }, [dbCashflow]);

  if (isLoading) return <Spinner />;

  return (
    <>
      <div className="article-main">
        <Grid>
          <div
            style={{
              background: "linear-gradient(to right, #343A40, #525252)",
              width: "96vw",
            }}
          >
            <Grid>
              <div onClick={() => setShowUserPasswordEdit(true)}>
                {Fox && (
                  <Image
                    width={100}
                    height={100}
                    source={Fox}
                    className="table-thumbnail stockinfo-icon"
                  />
                )}
              </div>

              <div
                style={{
                  marginTop: "10px",
                  marginLeft: "20px",
                  width: "50%",
                  overflow: "hidden",
                }}
              >
                <h4 className="text-light text-truncate">{data?.name}</h4>
                <span className="text-muted">{data.slogan || ""}</span>
              </div>
            </Grid>
          </div>
        </Grid>

        {/* KeyFacts */}
        <br />
        <div>
          <Grid>
            <div className="col-6 col-lg-3 text-center portfolio-dashboard-grid">
              <div className="rounded portfolio-dashboard-keyfacts">
                <FontAwesomeIcon
                  icon={faMoneyBillTrendUp}
                  className="text-muted"
                />
                <p className="stockinfo-keyfacts-value text-lawngreen">
                  {`${moment(data?.investorSince).format("DD.MM.YYYY")}`}
                </p>
                <p className="portfolio-keyfacts-label text-muted">
                  Investiert seit
                </p>
              </div>
            </div>

            <div className="col-6 col-lg-3 text-center portfolio-dashboard-grid">
              <div className="rounded portfolio-dashboard-keyfacts">
                <FontAwesomeIcon icon={faTrophy} className="text-muted" />
                <p className="stockinfo-keyfacts-value text-lawngreen">{`${100 - (data?.percentile || 0)} %`}</p>
                <p className="portfolio-keyfacts-label text-muted">
                  Top % deiner Altersgruppe
                </p>
              </div>
            </div>

            <div className="col-6 col-lg-3 text-center portfolio-dashboard-grid">
              <div className="rounded portfolio-dashboard-keyfacts">
                <FontAwesomeIcon
                  icon={faSquareRootVariable}
                  className="text-muted"
                />
                <p className="stockinfo-keyfacts-value text-lawngreen">{`${data?.medianMultiple}x`}</p>
                <p className="portfolio-keyfacts-label text-muted">
                  Medianvermögen
                </p>
              </div>
            </div>

            <div className="col-6 col-lg-3 text-center portfolio-dashboard-grid">
              <div className="rounded portfolio-dashboard-keyfacts">
                <FontAwesomeIcon icon={faForward} className="text-muted" />
                <p className="stockinfo-keyfacts-value text-lawngreen">{`${data?.aheadYears}`}</p>
                <p className="portfolio-keyfacts-label text-muted">
                  Jahre Vorsprung
                </p>
              </div>
            </div>
          </Grid>
        </div>

        {/* Personal data */}
        <Grid>
          <div className="col-12 text-left">
            <p className="stockinfo-keyfacts-label text-muted">Email</p>
            <p
              className="stockinfo-keyfacts-value text-light"
              style={{ fontSize: "14px" }}
            >
              {data.mail}
            </p>
          </div>
        </Grid>

        <Grid>
          {/* Description */}
          {data?.goal && (
            <div className="article-main">
              <h6 className="text-muted">Investmentziel</h6>
              <div id="meta-description" className="text-light text-left">
                {parse(data?.goal)}
              </div>
            </div>
          )}

          {dbCashflow && Object.keys(dbCashflow).length > 0 && (
            <div className="article-main">
              <h6 className="text-muted">Cashflow</h6>
              <div id="meta-description" className="text-light text-left">
                <p className="text-light">
                  Du hast ein monatliches Budget von
                  <b className="visualizeLink linkIsInformative">{` ${formatNumber(
                    totalAmount?.income.toFixed(2),
                  )}€ `}</b>
                  und deine fixen Ausgaben belaufen sich auf
                  <b className="visualizeLink linkIsInformative">
                    {` ${formatNumber(totalAmount?.expense?.toFixed(2))}€`}
                  </b>
                  .&nbsp;
                  <CashflowDescription
                    income={totalAmount?.income}
                    expense={totalAmount?.expense}
                  />
                </p>
                {/* Sankey Chart */}
                {sankeyChartData && totalAmount && (
                  <Charts.Sankey data={sankeyChartData} />
                )}
              </div>
            </div>
          )}
        </Grid>
      </div>
      {/** ################################ */}
      <div
        className={`offcanvas offcanvas-bottom article-secondary-mobile ${
          showUserPasswordEdit && "article-secondary-visible show"
        }`}
        tabIndex="-1"
        id="offcanvasBottom"
        aria-labelledby="offcanvasLabel"
      >
        <div className="offcanvas-header">
          <button
            type="button"
            className="btn-close-custom dismiss-active-or-focus"
          >
            <i
              className="icon-click icon-close bi bi-x-lg text-muted"
              onClick={() => setShowUserPasswordEdit((prev) => !prev)}
            />
          </button>
        </div>
        <div className="offcanvas-body article-secondary-scroll">
          <div className="article-secondary-info">
            <Image source={Fox} className="article-logo" />

            <h4 className="text-dark text-muted text-truncate">
              Passwort ändern
            </h4>

            <div
              id="data-description"
              className="text-muted text-left article-base-description-active"
            >
              <form id="updatePassword">
                <div className="form-group col-12">
                  <label className="text-dark" for="old">
                    Altes Passwort
                  </label>
                  <div className="input-group">
                    <input
                      type="password"
                      className="form-control"
                      id="old"
                      name="old"
                      value={changePasswordFormItem.old}
                      onChange={(e) =>
                        setChangePasswordFormItem((prev) => ({
                          ...prev,
                          old: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="form-group col-12">
                  <label className="text-dark" for="new">
                    Neues Passwort
                  </label>
                  <div className="input-group">
                    <input
                      type="password"
                      className="form-control"
                      id="new"
                      name="new"
                      value={changePasswordFormItem.new}
                      onChange={(e) =>
                        setChangePasswordFormItem((prev) => ({
                          ...prev,
                          new: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="form-group col-12">
                  <label className="text-dark" for="repeat">
                    Neues Passwort wiederholen
                  </label>
                  <div className="input-group">
                    <input
                      type="password"
                      className="form-control"
                      id="repeat"
                      name="repeat"
                      value={changePasswordFormItem.repeat}
                      onChange={(e) =>
                        setChangePasswordFormItem((prev) => ({
                          ...prev,
                          repeat: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div style={{ marginLeft: "15px" }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ marginRight: "5px" }}
                    onClick={() => {
                      setChangePasswordFormItem({});
                      setShowUserPasswordEdit(false);
                    }}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-search-custom dismiss-active-or-focus"
                    onClick={async (e) => {
                      e.preventDefault(); //

                      if (
                        changePasswordFormItem.old &&
                        changePasswordFormItem.new &&
                        changePasswordFormItem.repeat
                      ) {
                        if (
                          changePasswordFormItem.new !==
                          changePasswordFormItem.repeat
                        ) {
                          createNotification(
                            "error",
                            "Bitte überprüfe neues Passwort.",
                          );
                          setChangePasswordFormItem((prev) => ({
                            ...prev,
                            new: null,
                            repeat: null,
                          }));
                          return;
                        }

                        changePassword(
                          changePasswordFormItem.old,
                          changePasswordFormItem.new,
                        )
                          .then((result) => {
                            createNotification("success", "Passwort geändert");
                            setShowUserPasswordEdit(false);
                          })
                          .catch((err) => {
                            createNotification("error", err.message);
                          });
                      } else {
                        createNotification("error", "Bitte alle Daten angeben");
                      }
                    }}
                  >
                    Passwort ändern
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Userprofile;
